"use client";

import { useEffect, useMemo, useRef, useState } from "react";

export type AccountStreamStatus = "connecting" | "connected" | "error";

export interface AccountPositionEvent {
  accountNumber: string;
  payload: unknown;
  publishedAt?: number;
  type: "position";
}

export interface AccountOrderEvent {
  accountNumber: string;
  payload: unknown;
  publishedAt?: number;
  type: "order";
}

export interface AccountBalanceEvent {
  accountNumber: string;
  payload: unknown;
  publishedAt?: number;
  type: "balance";
}

export interface UseAccountEventStreamOptions {
  includePositions?: boolean;
  includeOrders?: boolean;
  includeBalances?: boolean;
}

interface AccountEventState {
  positions: Record<string, AccountPositionEvent | undefined>;
  orders: Record<string, AccountOrderEvent | undefined>;
  balances: Record<string, AccountBalanceEvent | undefined>;
  status: Record<string, AccountStreamStatus>;
  errors: Record<string, string | null>;
}

const initialState: AccountEventState = {
  positions: {},
  orders: {},
  balances: {},
  status: {},
  errors: {},
};

function buildTypesParam(options: UseAccountEventStreamOptions): string {
  const segments: string[] = [];
  if (options.includePositions !== false) {
    segments.push("positions");
  }
  if (options.includeOrders) {
    segments.push("orders");
  }
  if (options.includeBalances) {
    segments.push("balances");
  }
  if (segments.length === 0) {
    segments.push("positions");
  }
  return segments.join(",");
}

export function useAccountEventStream(
  accounts: ReadonlyArray<string>,
  options: UseAccountEventStreamOptions = {},
): AccountEventState {
  const normalizedAccounts = useMemo(() => {
    const set = new Set<string>();
    accounts.forEach((value) => {
      if (!value) return;
      const trimmed = value.trim();
      if (!trimmed) return;
      set.add(trimmed);
    });
    return Array.from(set);
  }, [accounts]);

  const normalizedOptions = useMemo(
    () => ({
      includePositions: options.includePositions,
      includeOrders: options.includeOrders,
      includeBalances: options.includeBalances,
    }),
    [options.includePositions, options.includeOrders, options.includeBalances],
  );
  const { includePositions, includeOrders, includeBalances } = normalizedOptions;

  const sourcesRef = useRef<Map<string, EventSource>>(new Map());
  const [positions, setPositions] = useState(initialState.positions);
  const [orders, setOrders] = useState(initialState.orders);
  const [balances, setBalances] = useState(initialState.balances);
  const [status, setStatus] = useState(initialState.status);
  const [errors, setErrors] = useState(initialState.errors);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const currentSources = sourcesRef.current;
    const desired = new Set(normalizedAccounts);

    currentSources.forEach((source, account) => {
      if (!desired.has(account)) {
        const cleanup = (source as unknown as { __cleanup?: () => void }).__cleanup;
        if (cleanup) cleanup();
        source.close();
        currentSources.delete(account);
        setStatus((prev) => {
          if (!(account in prev)) return prev;
          const next = { ...prev };
          delete next[account];
          return next;
        });
        setErrors((prev) => {
          if (!(account in prev)) return prev;
          const next = { ...prev };
          delete next[account];
          return next;
        });
      }
    });

    normalizedAccounts.forEach((accountNumber) => {
      if (currentSources.has(accountNumber)) {
        return;
      }

      if (!accountNumber) {
        return;
      }

      try {
        const eventTypes = buildTypesParam({
          includePositions,
          includeOrders,
          includeBalances,
        });
        const eventSource = new EventSource(
          `/api/market-data/stream?account=${encodeURIComponent(
            accountNumber,
          )}&types=${encodeURIComponent(eventTypes)}`,
        );

        currentSources.set(accountNumber, eventSource);
        setStatus((prev) => ({ ...prev, [accountNumber]: "connecting" }));
        setErrors((prev) => ({ ...prev, [accountNumber]: null }));

        const handleEvent = (event: MessageEvent, label: "position" | "order" | "balance") => {
          try {
            const payload = JSON.parse(event.data);
            const publishedAt =
              typeof payload?.publishedAt === "number" ? payload.publishedAt : Date.now();
            const accountValue =
              typeof payload?.accountNumber === "string"
                ? payload.accountNumber
                : accountNumber;

            const base = {
              accountNumber: accountValue,
              payload: payload?.payload ?? payload,
              publishedAt,
              type: label,
            } as AccountPositionEvent | AccountOrderEvent | AccountBalanceEvent;

            if (label === "position") {
              setPositions((prev) => ({ ...prev, [accountNumber]: base as AccountPositionEvent }));
            }
            if (label === "order") {
              setOrders((prev) => ({ ...prev, [accountNumber]: base as AccountOrderEvent }));
            }
            if (label === "balance") {
              setBalances((prev) => ({ ...prev, [accountNumber]: base as AccountBalanceEvent }));
            }

            setStatus((prev) => ({ ...prev, [accountNumber]: "connected" }));
            setErrors((prev) => ({ ...prev, [accountNumber]: null }));
          } catch (error) {
            console.error(
              `[useAccountEventStream] Failed to parse ${label} message for ${accountNumber}`,
              error,
            );
          }
        };

        const priceListener = (event: MessageEvent) => handleEvent(event, "position");
        const orderListener = (event: MessageEvent) => handleEvent(event, "order");
        const balanceListener = (event: MessageEvent) => handleEvent(event, "balance");

        eventSource.addEventListener("position", priceListener as EventListener);
        eventSource.addEventListener("order", orderListener as EventListener);
        eventSource.addEventListener("balance", balanceListener as EventListener);

        eventSource.onopen = () => {
          setStatus((prev) => ({ ...prev, [accountNumber]: "connected" }));
          setErrors((prev) => ({ ...prev, [accountNumber]: null }));
        };

        eventSource.onerror = () => {
          setStatus((prev) => ({ ...prev, [accountNumber]: "error" }));
          setErrors((prev) => ({ ...prev, [accountNumber]: "Connection error" }));
        };

        const handleMessage = (event: MessageEvent) => {
          try {
            const payload = JSON.parse(event.data);
            const type = typeof payload?.type === "string" ? payload.type.toLowerCase() : "";
            if (type === "position" || type === "positions") {
              handleEvent(event, "position");
              return;
            }
            if (type === "order" || type === "orders") {
              handleEvent(event, "order");
              return;
            }
            if (type === "balance" || type === "balances") {
              handleEvent(event, "balance");
            }
          } catch {
            // Ignore generic messages we cannot parse
          }
        };

        eventSource.onmessage = handleMessage as EventListener;

        const cleanup = () => {
          eventSource.removeEventListener("position", priceListener as EventListener);
          eventSource.removeEventListener("order", orderListener as EventListener);
          eventSource.removeEventListener("balance", balanceListener as EventListener);
        };

        (eventSource as unknown as { __cleanup?: () => void }).__cleanup = cleanup;
      } catch (error) {
        console.error(
          `[useAccountEventStream] Failed to establish stream for ${accountNumber}`,
          error,
        );
      }
    });

    return () => {
      currentSources.forEach((source) => {
        const cleanup = (source as unknown as { __cleanup?: () => void }).__cleanup;
        if (cleanup) cleanup();
        source.close();
      });
      currentSources.clear();
      setPositions({});
      setOrders({});
      setBalances({});
      setStatus({});
      setErrors({});
    };
  }, [normalizedAccounts, includePositions, includeOrders, includeBalances]);

  return {
    positions,
    orders,
    balances,
    status,
    errors,
  };
}
